import NiceModal, { useModal } from '@ebay/nice-modal-react'
import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography
} from '@mui/material'
import { CompanyUsersRelation } from '@shared/types/companies.types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useUpdateCompanyUserMutation } from '../../hooks/useMutations/useCompaniesMutations'
import { useSnackbar } from '../../hooks/useSnackbar'
import { LoaderButton } from '../LoaderButton'

export const ChangeUserRelationModal = NiceModal.create(
  ({
    companyId,
    userId,
    currentRelation,
    workspaceId
  }: {
    companyId: string
    userId: string
    currentRelation: CompanyUsersRelation
    workspaceId?: string
  }) => {
    const modal = useModal()
    const { t } = useTranslation()
    const [newRelation, setNewRelation] = useState<
      CompanyUsersRelation | undefined
    >(undefined)
    const { showSnackbar } = useSnackbar()

    const updateCompanyUserMutation = useUpdateCompanyUserMutation(
      companyId,
      workspaceId
    )

    const handleSave = () => {
      updateCompanyUserMutation.mutate(
        {
          userId,
          data: { relation: newRelation }
        },
        {
          onSuccess: () => {
            showSnackbar({
              message: t('change_relation_modal.success'),
              severity: 'success'
            })
          },
          onError: () => {
            showSnackbar({
              message: t('change_relation_modal.error'),
              severity: 'error'
            })
          },
          onSettled: () => {
            modal.remove()
          }
        }
      )
    }

    return (
      <Dialog
        open={modal.visible}
        onClose={() => modal.remove()}
        sx={{
          '& .MuiDialog-paper': {
            width: '32.5625rem',
            padding: '1.5rem',
            borderRadius: '.25rem'
          }
        }}
      >
        <DialogTitle sx={{ py: '1.5rem' }}>
          {t('change_relation_modal.title')}
        </DialogTitle>
        <DialogContent sx={{ padding: '1.5rem' }}>
          <Stack direction='column' spacing='1.5rem'>
            <Typography variant='body1'>
              {t('change_relation_modal.description')}
            </Typography>
            <FormControl fullWidth>
              <InputLabel id='relation-select-label'>
                {t('change_relation_modal.placeholder')}
              </InputLabel>
              <Select
                labelId='relation-select-label'
                label={t('change_relation_modal.placeholder')}
                value={newRelation || ''}
                onChange={e =>
                  setNewRelation(e.target.value as CompanyUsersRelation)
                }
              >
                {Object.values(CompanyUsersRelation)
                  .filter(relation => relation !== currentRelation)
                  .map(relation => (
                    <MenuItem key={relation} value={relation}>
                      {t(`relation.${relation.toLowerCase()}`)}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            {workspaceId && (
              <>
                <Alert severity='warning'>
                  <AlertTitle>
                    {t('change_relation_modal.alert_title')}
                  </AlertTitle>
                  {t('change_relation_modal.alert_description')}
                </Alert>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant='outlined'
            color='secondary'
            onClick={() => modal.remove()}
            sx={{
              textTransform: 'none',
              width: '14rem',
              padding: '.5rem 1.375rem',
              color: 'secondary.light',
              backgroundColor: 'custom.semiTransparentIndigo'
            }}
          >
            {t('common.cancel')}
          </Button>
          <LoaderButton
            variant='contained'
            loading={updateCompanyUserMutation.isPending}
            label={
              updateCompanyUserMutation.isPending
                ? t('change_relation_modal.changing_relation')
                : t('change_relation_modal.change_relation')
            }
            onClick={handleSave}
            sx={{
              width: '14rem',
              textTransform: 'capitalize',
              padding: '.5rem 1.375rem',
              color: 'rgba(0, 0, 0, 0.87)'
            }}
            disabled={!newRelation || updateCompanyUserMutation.isPending}
          />
        </DialogActions>
      </Dialog>
    )
  }
)
